import React, { useState } from "react";
import { Button, Chip, Grid, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import WorkOutlineIcon from "@material-ui/icons/WorkOutline";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import MemoryIcon from "@material-ui/icons/Memory";
import GetAppOutlinedIcon from "@material-ui/icons/GetAppOutlined";
import CardMembershipOutlinedIcon from "@material-ui/icons/CardMembershipOutlined";
import LaunchIcon from "@material-ui/icons/Launch";

import Title from "../components/title";
import { getTranslate } from "../localization";

const useStyles = makeStyles((theme) => ({
  "@keyframes timelineReveal": {
    from: {
      opacity: 0,
      transform:
        theme.direction === "rtl"
          ? "translate3d(12px,10px,0)"
          : "translate3d(-12px,10px,0)",
    },
    to: { opacity: 1, transform: "translate3d(0,0,0)" },
  },
  "@keyframes cardSheen": {
    from: {
      transform:
        theme.direction === "rtl"
          ? "translateX(145%) skewX(-18deg)"
          : "translateX(-145%) skewX(-18deg)",
    },
    to: {
      transform:
        theme.direction === "rtl"
          ? "translateX(-185%) skewX(-18deg)"
          : "translateX(185%) skewX(-18deg)",
    },
  },
  root: {
    width: "100%",
    minHeight: "100vh",
    padding: "52px 44px 90px",
    direction: theme.direction,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    [theme.breakpoints.down("sm")]: { padding: "34px 22px 64px" },
    [theme.breakpoints.down("xs")]: { padding: "28px 16px 56px" },
  },
  inner: {
    width: "min(100%, 1280px)",
    margin: "0 auto",
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    direction: theme.direction,
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: 0,
    },
  },
  downloadButton: {
    minHeight: 46,
    flexShrink: 0,
    padding: "8px 17px",
    marginTop: 3,
    borderRadius: 12,
    color: "var(--text-soft)",
    fontSize: 11,
    fontWeight: 850,
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
    boxShadow: "var(--shadow-sm)",
    transition: "transform .2s ease, border-color .2s ease, background-color .2s ease",
    "&:hover": {
      color: "var(--text-primary)",
      borderColor: "var(--border-accent)",
      background: "var(--accent-soft)",
      transform: "translateY(-2px)",
    },
    [theme.breakpoints.down("xs")]: { marginBottom: 34 },
  },
  buttonInner: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    direction: theme.direction,
  },
  section: { marginBottom: 58 },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 25,
    direction: theme.direction,
  },
  headingIconWrap: {
    width: 46,
    height: 46,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "var(--accent)",
    borderRadius: 14,
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  headingIcon: { fontSize: 27 },
  headingText: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(1.45rem,2.4vw,2rem)",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  timeline: {
    position: "relative",
    paddingLeft: theme.direction === "rtl" ? 0 : 44,
    paddingRight: theme.direction === "rtl" ? 44 : 0,
    "&::before": {
      content: '\"\"',
      position: "absolute",
      top: 21,
      bottom: 21,
      width: 2,
      left: theme.direction === "rtl" ? "auto" : 9,
      right: theme.direction === "rtl" ? 9 : "auto",
      borderRadius: 999,
      background: "linear-gradient(var(--accent),rgba(22,139,255,.10))",
      opacity: .52,
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.direction === "rtl" ? 0 : 31,
      paddingRight: theme.direction === "rtl" ? 31 : 0,
      "&::before": {
        left: theme.direction === "rtl" ? "auto" : 6,
        right: theme.direction === "rtl" ? 6 : "auto",
      },
    },
  },
  timelineItem: {
    position: "relative",
    marginBottom: 18,
    opacity: 0,
    animation: "$timelineReveal .55s cubic-bezier(.2,.75,.2,1) forwards",
    animationDelay: "var(--timeline-delay, 0ms)",
    "&:last-child": { marginBottom: 0 },
    "&:hover $marker": {
      borderColor: "var(--accent)",
      boxShadow: "0 0 0 6px var(--accent-soft),0 0 20px rgba(22,139,255,.23)",
    },
  },
  marker: {
    position: "absolute",
    top: 28,
    width: 19,
    height: 19,
    left: theme.direction === "rtl" ? "auto" : -44,
    right: theme.direction === "rtl" ? -44 : "auto",
    borderRadius: "50%",
    background: "var(--page-bg)",
    border: "4px solid color-mix(in srgb, var(--accent) 55%, var(--text-muted))",
    boxShadow: "0 0 0 4px var(--page-bg)",
    transition: "border-color .2s ease, box-shadow .2s ease",
    zIndex: 2,
    [theme.breakpoints.down("xs")]: {
      width: 15,
      height: 15,
      top: 29,
      left: theme.direction === "rtl" ? "auto" : -31,
      right: theme.direction === "rtl" ? -31 : "auto",
      borderWidth: 3,
    },
  },
  experienceCard: {
    position: "relative",
    minWidth: 0,
    padding: "20px 22px",
    borderRadius: 20,
    overflow: "hidden",
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
    transition: "transform .22s ease, border-color .22s ease, box-shadow .22s ease",
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: "var(--border-accent)",
      boxShadow: "var(--shadow-md)",
    },
    "&::before": {
      content: '\"\"',
      position: "absolute",
      zIndex: 0,
      top: -30,
      bottom: -30,
      width: "22%",
      pointerEvents: "none",
      background: "linear-gradient(90deg,transparent,rgba(92,190,255,.10),transparent)",
      transform:
        theme.direction === "rtl"
          ? "translateX(145%) skewX(-18deg)"
          : "translateX(-145%) skewX(-18deg)",
    },
    "&:hover::before": {
      animation: "$cardSheen .78s cubic-bezier(.2,.8,.2,1)",
    },
    "&::after": {
      content: '\"\"',
      position: "absolute",
      top: 0,
      bottom: 0,
      width: 3,
      left: theme.direction === "rtl" ? "auto" : 0,
      right: theme.direction === "rtl" ? 0 : "auto",
      background: "linear-gradient(var(--accent),transparent 65%)",
      opacity: .8,
    },
    [theme.breakpoints.down("xs")]: { padding: "17px 15px" },
  },
  cardLayout: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
    minWidth: 0,
    [theme.breakpoints.down("xs")]: { gap: 12 },
  },
  logoBox: {
    width: 62,
    height: 62,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: 0,
    borderRadius: 15,
    background: "transparent",
    border: 0,
    boxShadow: "none",
    [theme.breakpoints.down("xs")]: { width: 54, height: 54, borderRadius: 13 },
  },
  logoBoxWide: {
    width: 110,
    height: 62,
    borderRadius: 13,
    [theme.breakpoints.down("xs")]: {
      width: 88,
      height: 50,
      borderRadius: 11,
    },
  },
  logo: {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "contain",
    objectPosition: "center",
    borderRadius: "inherit",
    imageRendering: "auto",
  },
  logoFallback: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    color: "#087ff5",
    background: "#eef7ff",
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: ".04em",
  },
  cardContent: { minWidth: 0, flex: 1 },
  date: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 9px",
    marginBottom: 9,
    borderRadius: 999,
    color: "var(--accent)",
    fontSize: 10,
    fontWeight: 800,
    lineHeight: 1.4,
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  itemTitle: {
    color: "var(--accent)",
    fontWeight: 900,
    fontSize: "clamp(1.08rem,2vw,1.45rem)",
    lineHeight: 1.5,
    marginBottom: 5,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  subtitle: {
    color: "var(--text-primary)",
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1.65,
    marginBottom: 4,
  },
  meta: {
    color: "var(--text-muted)",
    fontSize: 11,
    fontWeight: 650,
    lineHeight: 1.7,
    marginBottom: 9,
  },
  content: {
    color: "var(--text-secondary)",
    fontSize: 12.7,
    lineHeight: 1.9,
    margin: 0,
    textAlign: theme.direction === "rtl" ? "right" : "left",
    overflowWrap: "break-word",
  },
  highlights: {
    margin: "11px 0 0",
    paddingLeft: theme.direction === "rtl" ? 0 : 19,
    paddingRight: theme.direction === "rtl" ? 19 : 0,
    color: "var(--text-secondary)",
    fontSize: 12,
    lineHeight: 1.82,
  },
  highlight: {
    marginBottom: 4,
    "&::marker": { color: "var(--accent)" },
  },
  skillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 13,
  },
  miniChip: {
    height: 25,
    color: "var(--text-soft)",
    fontSize: 8.8,
    background: "var(--surface-soft)",
    border: "1px solid var(--border)",
  },
  certificationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: 14,
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
  },
  certificationCard: {
    position: "relative",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: 20,
    borderRadius: 20,
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
    transition: "transform .22s ease, border-color .22s ease, box-shadow .22s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      borderColor: "var(--border-accent)",
      boxShadow: "var(--shadow-md)",
    },
  },
  certificationHead: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 13,
    minWidth: 0,
  },
  certificationCopy: { minWidth: 0, flex: 1 },
  certificationTitle: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: 15,
    lineHeight: 1.55,
    marginBottom: 3,
    overflowWrap: "break-word",
  },
  certificationIssuer: {
    color: "var(--text-soft)",
    fontWeight: 750,
    fontSize: 11.5,
    lineHeight: 1.6,
  },
  certificationDate: {
    marginTop: 8,
    color: "var(--text-muted)",
    fontSize: 10.5,
    lineHeight: 1.65,
  },
  credentialId: {
    marginTop: 3,
    color: "var(--text-muted)",
    fontSize: 10,
    lineHeight: 1.65,
    overflowWrap: "anywhere",
  },
  credentialButton: {
    alignSelf: theme.direction === "rtl" ? "flex-end" : "flex-start",
    marginTop: 14,
    minHeight: 36,
    padding: "5px 11px",
    borderRadius: 10,
    color: "var(--text-soft)",
    fontSize: 9.5,
    fontWeight: 850,
    border: "1px solid var(--border)",
    background: "var(--surface-soft)",
    "&:hover": {
      color: "var(--accent)",
      borderColor: "var(--border-accent)",
      background: "var(--accent-soft)",
    },
  },
  researchCard: {
    position: "relative",
    padding: 26,
    borderRadius: 22,
    overflow: "hidden",
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border-accent)",
    boxShadow: "var(--shadow-md)",
    [theme.breakpoints.down("xs")]: { padding: 18 },
  },
  researchTitle: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(1.25rem,2.1vw,1.7rem)",
    marginBottom: 11,
    lineHeight: 1.5,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  researchDescription: {
    color: "var(--text-secondary)",
    lineHeight: 1.95,
    fontSize: 13,
    marginBottom: 18,
    maxWidth: 960,
  },
  researchTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 7,
    marginBottom: 22,
  },
  chip: {
    color: "var(--text-soft)",
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
    fontSize: 10,
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(4,minmax(0,1fr))",
    gap: 10,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  stat: {
    padding: 15,
    minWidth: 0,
    borderRadius: 14,
    background: "var(--surface-soft)",
    border: "1px solid var(--border)",
  },
  statValue: {
    color: "var(--accent)",
    fontSize: 18,
    fontWeight: 900,
    marginBottom: 4,
    overflowWrap: "anywhere",
  },
  statLabel: {
    color: "var(--text-muted)",
    fontSize: 10,
    lineHeight: 1.65,
  },
  skillCard: {
    height: "100%",
    padding: 21,
    borderRadius: 19,
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  skillTitle: {
    color: "var(--text-primary)",
    fontWeight: 850,
    marginBottom: 15,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  chipWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
}));

function ItemLogo({ item, classes }) {
  const [failed, setFailed] = useState(false);
  const fallback = item.logoFallback || item.title.slice(0, 2).toUpperCase();

  return (
    <div
      className={`${classes.logoBox} ${item.logoWide ? classes.logoBoxWide : ""}`}
      aria-hidden="true"
    >
      {!failed && item.logo ? (
        <img src={item.logo} alt="" className={classes.logo} onError={() => setFailed(true)} />
      ) : (
        <div className={classes.logoFallback}>{fallback}</div>
      )}
    </div>
  );
}

export default function ResumeScreen() {
  const classes = useStyles();
  const translate = getTranslate();

  const renderTimeline = (items) => (
    <div className={classes.timeline}>
      {items.map((item, index) => (
        <div
          className={classes.timelineItem}
          key={item.id}
          style={{ "--timeline-delay": `${Math.min(index, 6) * 80}ms` }}
        >
          <span className={classes.marker} aria-hidden="true" />
          <article className={classes.experienceCard}>
            <div className={classes.cardLayout}>
              <ItemLogo item={item} classes={classes} />

              <div className={classes.cardContent}>
                <div className={classes.date}>{item.date}</div>
                <Typography component="h3" className={classes.itemTitle}>{item.title}</Typography>
                <div className={classes.subtitle}>{item.subtitle}</div>
                {item.meta ? <div className={classes.meta}>{item.meta}</div> : null}
                {item.content ? <p className={classes.content}>{item.content}</p> : null}

                {item.highlights && item.highlights.length ? (
                  <ul className={classes.highlights}>
                    {item.highlights.map((highlight) => (
                      <li key={highlight} className={classes.highlight}>{highlight}</li>
                    ))}
                  </ul>
                ) : null}

                {item.skills && item.skills.length ? (
                  <div className={classes.skillRow}>
                    {item.skills.map((skill) => (
                      <Chip key={skill} label={skill} variant="outlined" className={classes.miniChip} />
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </article>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`${classes.root} modern-page`}>
      <div className={classes.inner}>
        <div className={classes.titleRow}>
          <Title title={translate.resumePageTitle} eyebrow={translate.resumeEyebrow} />
          <Button component="a" href="/Mehdi_Talebi_Resume.pdf" download className={classes.downloadButton}>
            <span className={classes.buttonInner}>
              <span>{translate.downloadcv}</span>
              <GetAppOutlinedIcon fontSize="small" />
            </span>
          </Button>
        </div>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><WorkOutlineIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.workingexperience}</Typography>
          </div>
          {renderTimeline(translate.workingSteps)}
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><SchoolOutlinedIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.educationalqualifications}</Typography>
          </div>
          {renderTimeline(translate.educationalSteps)}
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><CardMembershipOutlinedIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.certificationsTitle}</Typography>
          </div>

          <div className={classes.certificationGrid}>
            {translate.certifications.map((item) => (
              <article className={classes.certificationCard} key={item.id}>
                <div className={classes.certificationHead}>
                  <ItemLogo item={item} classes={classes} />
                  <div className={classes.certificationCopy}>
                    <div className={classes.certificationTitle}>{item.title}</div>
                    <div className={classes.certificationIssuer}>{item.issuer}</div>
                    <div className={classes.certificationDate}>{item.date}</div>
                    {item.credentialId ? (
                      <div className={classes.credentialId}>{translate.credentialIdLabel}: {item.credentialId}</div>
                    ) : null}
                  </div>
                </div>

                {item.skills && item.skills.length ? (
                  <div className={classes.skillRow}>
                    {item.skills.map((skill) => (
                      <Chip key={skill} label={skill} variant="outlined" className={classes.miniChip} />
                    ))}
                  </div>
                ) : null}

                {item.href ? (
                  <Button
                    component="a"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.credentialButton}
                  >
                    <span className={classes.buttonInner}>
                      <span>{translate.showCredential}</span>
                      <LaunchIcon fontSize="small" />
                    </span>
                  </Button>
                ) : null}
              </article>
            ))}
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><MemoryIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.researchSpotlight}</Typography>
          </div>

          <div className={classes.researchCard}>
            <Typography component="h3" className={classes.researchTitle}>{translate.researchTitle}</Typography>
            <div className={classes.researchDescription}>{translate.researchDescription}</div>

            <div className={classes.researchTags}>
              {translate.researchTags.map((tag) => (
                <Chip key={tag} label={tag} variant="outlined" className={classes.chip} />
              ))}
            </div>

            <div className={classes.stats}>
              {translate.researchStats.map((item) => (
                <div className={classes.stat} key={item.label}>
                  <div className={classes.statValue}>{item.value}</div>
                  <div className={classes.statLabel}>{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={classes.section}>
          <div className={classes.heading}>
            <div className={classes.headingIconWrap}><MemoryIcon className={classes.headingIcon} /></div>
            <Typography component="h2" className={classes.headingText}>{translate.myskills}</Typography>
          </div>

          <Grid container spacing={2}>
            {translate.skillGroups.map((group) => (
              <Grid item xs={12} md={6} key={group.title}>
                <div className={classes.skillCard}>
                  <Typography variant="h5" className={classes.skillTitle}>{group.title}</Typography>
                  <div className={classes.chipWrap}>
                    {group.items.map((item) => (
                      <Chip variant="outlined" className={classes.chip} label={item} key={item} />
                    ))}
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </section>
      </div>
    </div>
  );
}
